import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PsychologyIcon from "@mui/icons-material/Psychology";
import RefreshIcon from "@mui/icons-material/Refresh";
import NewColors from "../../../../../themes/NewColors";

const AIInsightsCard = ({
  response,
  loading,
  error,
  onRetry,
  onExpandSection,
}) => {
  const hasValidData =
    response &&
    typeof response === "object" &&
    typeof response.title === "string" &&
    response.title.trim() !== "" &&
    typeof response.summary === "string" &&
    response.summary.trim() !== "";

  const showIdleHint = !loading && !hasValidData && !error;

  return (
    <Card
      sx={(theme) => ({
        mb: 2,
        bgcolor: NewColors.cardBackground,
        borderRadius: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        overflow: "hidden",
      })}
    >
      <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
        <Accordion
          disableGutters
          elevation={0}
          onChange={(_event, expanded) => {
            if (expanded) {
              onExpandSection?.();
            }
          }}
          sx={{
            boxShadow: "none",
            bgcolor: "transparent",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="ai-insights-content"
            id="ai-insights-header"
            sx={{
              px: 2,
              py: 1.5,
              minHeight: 0,
              "& .MuiAccordionSummary-content": { my: 1 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mr: 1,
                  minWidth: "115px",
                }}
              >
                <PsychologyIcon sx={{ mr: 1 }} />
                <Typography variant="h6">AI Insights:</Typography>
              </Box>
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    Generating insights...
                  </Typography>
                </Box>
              ) : showIdleHint ? (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    fontStyle: "italic",
                    flexGrow: 1,
                    flexShrink: 1,
                  }}
                >
                  Expand this section to load AI insights for this report
                </Typography>
              ) : hasValidData ? (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    fontStyle: "italic",
                    flexGrow: 1,
                    flexShrink: 1,
                    display: "flex",
                    alignItems: "center",
                    flexBasis: { xs: "100%", md: "auto" },
                    paddingTop: { xs: 0, md: "3px" },
                  }}
                >
                  {response.title}
                </Typography>
              ) : error ? (
                <Typography
                  variant="body1"
                  color="warning.main"
                  sx={{
                    fontStyle: "italic",
                    flexGrow: 1,
                    flexShrink: 1,
                  }}
                >
                  {error}
                </Typography>
              ) : (
                <Typography
                  variant="body1"
                  color="error"
                  sx={{
                    fontStyle: "italic",
                    flexGrow: 1,
                    flexShrink: 1,
                  }}
                >
                  Invalid or missing insight data
                </Typography>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress />
              </Box>
            ) : hasValidData ? (
              <Box
                sx={{
                  mt: 2,
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  "& h1, & h2": {
                    color: "primary.main",
                    marginTop: "16px",
                    marginBottom: "10px",
                    fontWeight: 500,
                    fontSize: "1.25rem",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    paddingBottom: "8px",
                  },
                  "& h3, & h4": {
                    color: "primary.dark",
                    marginTop: "16px",
                    marginBottom: "8px",
                    fontWeight: 500,
                    fontSize: "1.125rem",
                  },
                  "& p": {
                    marginBottom: "12px",
                    lineHeight: 1.7,
                    fontSize: "0.95rem",
                  },
                  "& ul, & ol": {
                    marginTop: "8px",
                    marginBottom: "16px",
                    paddingLeft: "24px",
                  },
                  "& li": {
                    marginBottom: "6px",
                    lineHeight: 1.6,
                  },
                  "& li:last-child": {
                    marginBottom: 0,
                  },
                  "& strong": {
                    color: "text.primary",
                    fontWeight: 600,
                  },
                  "& a": {
                    color: "primary.main",
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  },
                  "& h2 + p, & h3 + p, & h2 + ul, & h3 + ul": {
                    backgroundColor: "rgba(0,0,0,0.02)",
                    padding: "12px 16px",
                    borderRadius: "4px",
                    marginBottom: "16px",
                  },
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: response.summary }} />
              </Box>
            ) : error ? (
              <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography color="warning.main" variant="body1">
                  {error}
                </Typography>
                <Typography
                  color="text.secondary"
                  variant="body2"
                  sx={{ mt: 1, mb: 2 }}
                >
                  Please try refreshing the page or check back later.
                </Typography>
                {onRetry && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={onRetry}
                    color="primary"
                  >
                    Try Again
                  </Button>
                )}
              </Box>
            ) : (
              <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography color="text.secondary" variant="body2">
                  Insights will appear here after they finish generating.
                </Typography>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default AIInsightsCard;
